<template>
    <validation-observer ref="marketing_settings_form" v-slot="{ meta, errors }">
        <div class="d-flex flex-column pt-3">
            <div class="d-flex flex-md-row flex-column">
                <validation-provider name="question" rules="required" class="w-100 mr-2" as="div">
                    <form-input
                        :leftIcon="['fas', 'location-dot']"
                        label="Question To Ask:"
                        :value="inquiry.fields[0].title"
                        :error="errors.question"
                        @input="onChangeQuestion"
                    />
                </validation-provider>
                <form-input
                    class="w-100"
                    :leftIcon="['fas', 'location-dot']"
                    label="Default Answer:"
                    disabled
                    :value="defaultAnswer"
                />
            </div>
            <form-textarea
                rows="6"
                label="Description"
                @input="onChangeDescription"
                :value="inquiry.fields[0].description"
            />
            <form-checkbox v-model="inquiry.fields[0].display_online"
                >Display question within online booking page?</form-checkbox
            >
            <form-checkbox v-model="inquiry.fields[0].display_guest"
                >Display field within Guest Control Panel?</form-checkbox
            >
            <div class="col-md-3 col-12 pl-0 pr-0">
                <span>Possible Answers</span>
                <div class="d-flex flex-column">
                    <draggable v-model="inquiry.fields[0].choices" group="answers">
                        <b-input-group
                            v-for="(answer, index) in inquiry.fields[0].choices"
                            :key="answer.id"
                            class="mt-3"
                        >
                            <b-input-group-prepend class="answer-wrapper" is-text>
                                <b-form-radio
                                    :checked="selectedAnswer"
                                    :value="answer.order"
                                    @change="selectAnswer(answer)"
                                    name="answer"
                                ></b-form-radio>
                            </b-input-group-prepend>
                            <b-form-input v-model="answer.title"></b-form-input>
                            <template #append v-if="inquiry.fields[0].choices.length !== 1">
                                <b-button
                                    variant="default"
                                    @click="$bvModal.show('delete-answer-modal' + answer.title + index)"
                                >
                                    <font-awesome-icon :icon="['fas', 'xmark']" />
                                </b-button>
                                <modal
                                    :id="'delete-answer-modal' + answer.title + index"
                                    title="Delete Answer"
                                    @ok="removeAnswer(answer.title)"
                                >
                                    Are you sure you want to delete answer? This action cannot be reversed! Answer order
                                    can be changed by dragging answer into new position.
                                </modal>
                            </template>
                        </b-input-group>
                    </draggable>
                    <b-button @click="addAnswer" variant="default" class="mt-3"
                        >Add Answer <font-awesome-icon :icon="['fas', 'plus']"
                    /></b-button>
                </div>
            </div>
            <b-button @click="saveSettings" class="mt-3 save-button mb-3" variant="primary" :disabled="!meta.valid"
                >Save</b-button
            >
        </div>
    </validation-observer>
</template>

<script>
    import API from '../../api';
    import { VueDraggable } from 'vue-draggable-plus';
    import FormInput from '../generic/form-input/form-input.vue';
    import FormCheckbox from '../generic/form-checkbox/form-checkbox.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import FormTextarea from '../generic/form-textarea/form-textarea.vue';
    import page from '../generic/page/page.vue';
    import { ISETTINGS_DATA, ISETTINGS_SAVE_MARKETING } from '../../endpoints';
    import Modal from '../generic/modal/modal.vue';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
    import { mapGetters } from 'vuex';
    import { watch } from 'vue';

    export default {
        components: {
            page,
            FormInput,
            FormCheckbox,
            FormSelect,
            FormTextarea,
            draggable: VueDraggable,
            Modal,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                busy: null,
                tabs: [
                    {
                        title: 'General',
                        route: '/app/isettings/general',
                        active: false,
                    },
                    {
                        title: 'Bookings',
                        route: '/app/isettings/bookings',
                        active: false,
                    },
                    {
                        title: 'Localization',
                        route: '/app/isettings/localization',
                        active: false,
                    },
                    {
                        title: 'Marketing',
                        route: '/app/isettings/marketing',
                        active: true,
                    },
                ],
            };
        },
        computed: {
            ...mapGetters('iSettings', ['inquiry', 'selectedAnswer', 'selectedAnswerId']),
            defaultAnswer() {
                return this.inquiry.fields[0].choices.find((item) => item.order === this.selectedAnswer)?.title;
            },
        },
        methods: {
            onChangeQuestion(value) {
                const inquiry = JSON.parse(JSON.stringify(this.inquiry));
                inquiry.fields[0].title = value;
                this.$store.commit('iSettings/setInquiry', inquiry);
            },
            onChangeDescription(value) {
                const inquiry = JSON.parse(JSON.stringify(this.inquiry));
                inquiry.fields[0].description = value;
                this.$store.commit('iSettings/setInquiry', inquiry);
            },
            addAnswer() {
                const inquiry = JSON.parse(JSON.stringify(this.inquiry));
                const count = inquiry.fields[0].choices.length;
                inquiry.fields[0].choices.push({
                    id: null,
                    order: count,
                    title: 'Answer #' + (count + 1),
                });
                this.$store.commit('iSettings/setInquiry', inquiry);
            },
            removeAnswer(title) {
                const inquiry = JSON.parse(JSON.stringify(this.inquiry));
                inquiry.fields[0].choices = inquiry.fields[0].choices.filter((item, i) => item.title !== title);
                const selectedAnswer = inquiry.fields[0].choices.length && inquiry.fields[0].choices[0].order;
                const selectedAnswerId = inquiry.fields[0].choices.length && inquiry.fields[0].choices[0].id;

                this.$store.commit('iSettings/setInquiry', inquiry);
                this.$store.commit('iSettings/setSelectedAnswer', selectedAnswer);
                this.$store.commit('iSettings/setSelectedAnswerId', selectedAnswerId);
            },
            async saveSettings() {
                try {
                    this.busy = true;
                    const answerArr = this.inquiry.fields[0].choices.map((item, index) => ({ ...item, order: index }));
                    const selectedAnswer = answerArr.find((item) => item.id === this.selectedAnswerId);
                    await API.post(ISETTINGS_SAVE_MARKETING(), {
                        ...this.inquiry,
                        fields: [
                            {
                                ...this.inquiry.fields[0],
                                choices: answerArr,
                                selected_index: selectedAnswer?.order,
                            },
                        ],
                    });
                    this.successToast({ text: 'Settings successfully updated.' });
                } catch (e) {
                    this.errorToast({ text: 'Could not update settings' });
                } finally {
                    this.busy = false;
                }
            },
            selectAnswer(answer) {
                this.$store.commit('iSettings/setSelectedAnswer', answer.order);
                this.$store.commit('iSettings/setSelectedAnswerId', answer.id);
            },
        },
        watch: {
            inquiry: function (value) {
                this.$refs.marketing_settings_form.setFieldValue('question', value.fields[0].title);
            },
        },
    };
</script>

<style lang="scss" scoped>
    .answer-wrapper {
        cursor: grabbing;
    }
</style>
